/**
 * useContactOrigin — de qual dos números da conta este contato VEIO (2026-08-06).
 *
 * A REGRA (decidida pelo Gustavo): o lead é medido por CLIENTE, não por canal. Um mesmo
 * telefone que escreveu para três das nossas caixas é UM contato, e a origem dele é a
 * caixa do PRIMEIRO vínculo. Quem tem dois telefones vira dois leads — e um deles morre
 * por falta de resposta, que é o comportamento certo.
 *
 * ── Por que um lote, e não uma chamada por card ─────────────────────────────────────
 * A lista de Contatos renderiza 15–25 cards por página. Um `GET` por card seriam 25
 * requisições para responder a uma coluna. Cada card se INSCREVE aqui e a inscrição é
 * acumulada; um `setTimeout(0)` fecha o lote e dispara UMA requisição com todos os ids.
 * Os cards montam no mesmo tick, então na prática o lote sempre sai inteiro.
 *
 * ── Cache com dono ──────────────────────────────────────────────────────────────────
 * O cache é de módulo (vive entre navegações — voltar para Contatos não re-consulta) e
 * carrega o accountId que o preencheu. Trocar de conta o descarta inteiro: sem isso, o
 * id 42 de uma conta mostraria a caixa do id 42 da outra.
 *
 * ── Ausência é uma resposta ─────────────────────────────────────────────────────────
 * `null` no cache significa "consultado e não há origem a mostrar" — contato cadastrado
 * à mão, ou origem numa caixa a que este usuário não tem acesso (o backend não a nomeia,
 * de propósito). `undefined` significa "ainda não consultado". A distinção existe para a
 * célula não piscar um esqueleto eterno em quem simplesmente não tem origem.
 */
import {
  reactive,
  ref,
  computed,
  watch,
  toValue,
  onMounted,
  onScopeDispose,
} from 'vue';
import contactOriginsAPI from 'dashboard/api/cw_custom/contactOrigins.js';

// contactId (Number) -> { inboxId, nome, canal, primeiroContatoEm } | null
const cache = reactive({});
let cacheAccountId = null;

let pendentes = new Set();
let timer = null;

function resetSeTrocouDeConta(accountId) {
  if (cacheAccountId === accountId) return;

  Object.keys(cache).forEach(k => delete cache[k]);
  pendentes = new Set();
  cacheAccountId = accountId;
}

function normalizar(bruto) {
  if (!bruto) return null;
  return {
    inboxId: bruto.inbox_id,
    nome: bruto.nome,
    canal: bruto.canal,
    primeiroContatoEm: bruto.primeiro_contato_em ?? null,
  };
}

async function despejar(accountId) {
  const ids = [...pendentes];
  pendentes = new Set();
  timer = null;
  if (!ids.length) return;

  try {
    const { data } = await contactOriginsAPI.fetchOrigins(accountId, ids);
    const origens = data?.origens ?? {};
    // Todo id pedido é gravado, inclusive os que voltaram vazios: é isso que transforma
    // "não sei" em "não há", e impede o card de pedir de novo a cada re-render.
    ids.forEach(id => {
      cache[id] = normalizar(origens[id]);
    });
  } catch (error) {
    // `axios` é global do Chatwoot e um estouro síncrono não passa pelo `.catch` de uma
    // promise — por isso try/catch e não `.catch()`. Falha de rede vira "sem origem":
    // a coluna some, o resto da tela segue. Nunca derrubar Contatos por causa dela.
    ids.forEach(id => {
      if (cache[id] === undefined) cache[id] = null;
    });
  }
}

function inscrever(id, accountId) {
  if (!id || !accountId) return;

  resetSeTrocouDeConta(accountId);
  if (cache[id] !== undefined || pendentes.has(id)) return;

  pendentes.add(id);
  if (timer) return;
  timer = setTimeout(() => despejar(accountId), 0);
}

/**
 * @param {Ref<Number>|Number} contactId
 * @param {Ref<Number>|Number} accountId
 * @returns {{ origem: ComputedRef<Object|null>, carregando: ComputedRef<Boolean> }}
 */
export function useContactOrigin(contactId, accountId) {
  const parar = watch(
    () => [toValue(contactId), toValue(accountId)],
    ([id, conta]) => inscrever(Number(id), conta),
    { immediate: true }
  );
  onScopeDispose(parar);

  const origem = computed(() => cache[Number(toValue(contactId))] ?? null);
  const carregando = computed(
    () => cache[Number(toValue(contactId))] === undefined
  );

  return { origem, carregando };
}

// ── O RESUMO DA CARTEIRA (ativos/total + ranking de origem) ─────────────────────────
//
// Duas telas leem o mesmo resumo: a faixa no topo de Contatos e o bloco na tela inicial
// da Fernanda. Sem estado compartilhado, abrir a Home e ir para Contatos dispararia a
// mesma consulta duas vezes, e — pior — as duas telas poderiam exibir números diferentes
// se um contato entrasse no intervalo. Uma leitura, uma verdade.
//
// A promessa em voo é guardada (`emVoo`), não só o resultado: dois componentes que montam
// no mesmo tick pegam a MESMA requisição em vez de disparar duas e a segunda vencer.
const resumo = ref(null);
let resumoAccountId = null;
let emVoo = null;

export function useContactsResumo(accountId) {
  const carregando = ref(resumo.value === null);

  async function carregar() {
    const conta = toValue(accountId);
    if (!conta) return;

    if (resumoAccountId !== conta) {
      resumo.value = null;
      emVoo = null;
      resumoAccountId = conta;
    }
    if (resumo.value !== null) {
      carregando.value = false;
      return;
    }

    try {
      // `axios` é global do Chatwoot: um estouro síncrono não passa pelo `.catch` de uma
      // promise, por isso try/catch. O resumo é acessório em toda tela que o usa — falhou,
      // o bloco não aparece e o resto segue.
      emVoo = emVoo ?? contactOriginsAPI.fetchSummary(conta);
      const { data } = await emVoo;
      resumo.value = data;
    } catch (error) {
      resumo.value = null;
    } finally {
      emVoo = null;
      carregando.value = false;
    }
  }

  onMounted(carregar);

  return { resumo, carregando };
}

// Exposto para os testes e para quem precise forçar releitura (ex.: após importação).
export function resetContactOrigins() {
  resumo.value = null;
  resumoAccountId = null;
  emVoo = null;
  Object.keys(cache).forEach(k => delete cache[k]);
  pendentes = new Set();
  cacheAccountId = null;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}
